// Header.jsx

import { useStore } from "../stores/store";
import { Bars3Icon } from "@heroicons/react/24/outline";
import SquawkPlayerButton from "./squawk-player/SquawkPlayerButton";
import EconomicCalendarCountdown from "../ui-components/EconomicCalendarCountdown";
import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import useAuth from "../hooks/useAuth";

const mediaQuery = window.matchMedia("(min-width: 1024px)");

const mobileMediaQuery = window.matchMedia("(max-width: 639px)");

const Header = () => {
  const { authenticated } = useAuth();
  const desktopSidebarOpen = useStore((state) => state.desktopSidebarOpen);

  const setDesktopSidebarOpen = useStore(
    (state) => state.setDesktopSidebarOpen
  );
  const setMobileSidebarOpen = useStore((state) => state.setMobileSidebarOpen);

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const squawkPlayerButton = (
    <Sentry.ErrorBoundary
      fallback={
        <p className="text-red-400 text-xs">Squawk Error. Please reload.</p>
      }
    >
      <SquawkPlayerButton />
    </Sentry.ErrorBoundary>
  );

  useEffect(() => {
    const handleChange = (e) => {
      setIsLargeScreen(e.matches);
    };

    // Initial match
    handleChange(mediaQuery);

    // Add listener
    mediaQuery.addEventListener("change", handleChange);

    // Cleanup
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const handleMobileChange = (e) => {
      setIsMobile(e.matches);
    };

    handleMobileChange(mobileMediaQuery);
    mobileMediaQuery.addEventListener("change", handleMobileChange);

    return () => mobileMediaQuery.removeEventListener("change", handleMobileChange);
  }, []);

  return (
    <header
      className={`bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50 h-12 sm:h-10 flex items-center px-2 transition-colors ${
        desktopSidebarOpen ? "lg:hidden" : ""
      }`}
    >
      <div className="flex w-full items-center justify-between">
        {/* Logo when sidebar is closed on desktop */}
        {!desktopSidebarOpen && (
          <div
            className="hidden lg:flex items-center cursor-pointer"
            onClick={() => setDesktopSidebarOpen(true)}
          >
            <img
              className="w-16"
              src="/newsquawk-logo-white.png"
              alt="Newsquawk Logo"
            />
          </div>
        )}

        {/* Hamburger menu on mobile */}
        <div className="lg:hidden">
          <button
            type="button"
            className="p-2.5 bg-transparent text-gray-700 dark:text-white"
            onClick={() => setMobileSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="mr-1 sm:mr-0 text-xs flex items-center gap-2">
          {/* Countdown Component - or Demo indicator on mobile when logged out */}
          {isMobile && !authenticated ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-md">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terminal-orange opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-terminal-orange" />
              </span>
              <span className="text-xs font-semibold text-terminal-orange uppercase">Demo</span>
            </div>
          ) : (
            <Sentry.ErrorBoundary fallback={<p className="text-red-500 text-xs">Calendar error</p>}>
              <EconomicCalendarCountdown onClick={(e) => e} />
            </Sentry.ErrorBoundary>
          )}

          {!desktopSidebarOpen && isLargeScreen && squawkPlayerButton}

          {!isLargeScreen && squawkPlayerButton}
        </div>
      </div>
    </header>
  );
};

export default Header;
